import get from 'lodash/get';
import { combineReducers } from 'redux';
import { createReducer } from 'redux-act';
import { createSelector, createSelectorCreator, lruMemoize } from 'reselect';
import shallowequal from 'shallowequal';
import { createResource } from '@gandi/resource/redux';
import { baseUrl as moduleBaseUrl, hashString, normalizeFqdn, statePath as moduleStatePath } from '../utils';
export const INSTANCE_STATUS_ACTIVE = 'active';
export const INSTANCE_STATUS_BEING_CREATED = 'being_created';
export const INSTANCE_STATUS_BEING_DELETED = 'being_deleted';
export const INSTANCE_STATUS_BEING_MIGRATED = 'being_migrated';
export const INSTANCE_STATUS_BEING_UPGRADED = 'being_upgraded';
export const INSTANCE_STATUS_DELETED = 'deleted';
export const INSTANCE_STATUS_LOCKED = 'locked';
export const INSTANCE_STATUS_PAUSED = 'paused';
export const INSTANCE_STATUS_RUNNING = 'running';
export const INSTANCE_STATUS_WAITING_BILL = 'waiting_bill';
export const INSTANCE_STATUS_WAITING_CONFIGURATION = 'waiting_configuration';
export const INSTANCE_SIZE_L = 'l';
export const INSTANCE_SIZE_M = 'm';
export const INSTANCE_SIZE_S = 's';
export const INSTANCE_SIZE_S_PLUS = 's+';
export const INSTANCE_SIZE_XL = 'xl';
export const INSTANCE_SIZE_XXL = 'xxl';
export const INSTANCE_TYPE_STATUS_BETA = 'beta';
export const INSTANCE_TYPE_STATUS_CURRENT = 'current';
export const INSTANCE_TYPE_STATUS_DELETED = 'deleted';
export const INSTANCE_TYPE_STATUS_DEPRECATED = 'deprecated';
export const INSTANCE_TYPE_STATUS_HIDDEN = 'hidden';
export const INSTANCE_DATABASE_MONGODB = 'mongodb';
export const INSTANCE_DATABASE_MYSQL = 'mysql';
export const INSTANCE_DATABASE_PGSQL = 'pgsql';
export const INSTANCE_LANGUAGE_NODEJS = 'nodejs';
export const INSTANCE_LANGUAGE_PHP = 'php';
export const INSTANCE_LANGUAGE_PYTHON = 'python';
export const INSTANCE_LANGUAGE_RUBY = 'ruby';
export const INSTANCE_DB_LANGUAGE_STATUS_BETA = 'beta';
export const INSTANCE_DB_LANGUAGE_STATUS_CURRENT = 'current';
export const INSTANCE_DB_LANGUAGE_STATUS_DELETED = 'deleted';
export const INSTANCE_DB_LANGUAGE_STATUS_DEPRECATED = 'deprecated';
export const INSTANCE_DB_LANGUAGE_STATUS_HIDDEN = 'hidden';
export const INSTANCE_ACTION_CONSOLE = 'console';
export const INSTANCE_ACTION_LOCK = 'lock';
export const INSTANCE_ACTION_RESET_PASSWORD = 'reset_database_password';
export const INSTANCE_ACTION_RESTART = 'restart';
export const INSTANCE_ACTION_UPGRADE = 'upgrade';
export const INSTANCE_ACTION_UNLOCK = 'unlock';
export const INSTANCE_UPGRADE_MODE_DATABASE = 'database';
export const INSTANCE_UPGRADE_MODE_LANGUAGE = 'language';
export const statePath = `${moduleStatePath}.instances`;
export const baseUrl = `${moduleBaseUrl}/instances`;
export const DEFAULT_PAGE = 1;
export const DEFAULT_PER_PAGE = 100;
export const DEFAULT_SORT_BY = '-created_at';
export const defaultParams = {
  page: DEFAULT_PAGE,
  per_page: DEFAULT_PER_PAGE,
  sort_by: DEFAULT_SORT_BY,
  search: {}
};
export const computeLabel = params => hashString(JSON.stringify({
  ...defaultParams,
  ...params
}));
export const resourceName = 'resources-simplehosting.instances';
export const resource = createResource(resourceName);
export const head = (params, options = {}) => dispatch => dispatch(resource.head(baseUrl, {
  params,
  ...options
}));
export const fetch = (params = {}, options = {}) => dispatch => {
  const mergedParams = {
    ...defaultParams,
    ...params
  };
  const label = computeLabel(mergedParams);
  return dispatch(resource.fetch(baseUrl, {
    label,
    params: {
      ...mergedParams,
      search: undefined,
      ...mergedParams.search
    },
    on200: response => response,
    not200: response => Promise.reject(response),
    ...options
  }));
};
export const fetchOne = (instanceId, options = {}) => dispatch => dispatch(resource.fetchOne(`${baseUrl}/${instanceId}`, {
  label: instanceId,
  ...options
}));
export const create = (payload, params = {}, options = {}) => dispatch => dispatch(resource.createOne(baseUrl, payload, {
  label: 'default',
  params,
  ...options
}));
export const patch = (instanceId, payload, options = {}) => dispatch => dispatch(resource.patchOne(`${baseUrl}/${instanceId}`, payload, {
  label: instanceId,
  ...options
}));
export const remove = (instanceId, params = {}, options = {}) => dispatch => dispatch(resource.deleteOne(`${baseUrl}/${instanceId}`, undefined, {
  label: instanceId,
  params,
  ...options
}));
export const postAction = (instanceId, action, payload, options = {}) => dispatch => dispatch(resource.post(`${baseUrl}/${instanceId}/action`, {
  action,
  ...payload
}, {
  label: `${instanceId}.${action}`,
  on202: response => response,
  not202: response => Promise.reject(response),
  ...options
}));
export const enableSnapshots = (instanceId, options) => dispatch => dispatch(patch(instanceId, {
  snapshot_enabled: true
}, {
  id: instanceId,
  label: `snapshot-activation-${instanceId}`,
  ...options
}));
export const disableSnapshots = (instanceId, options) => dispatch => dispatch(patch(instanceId, {
  snapshot_enabled: false
}, {
  id: instanceId,
  label: `snapshot-activation-${instanceId}`,
  ...options
}));
export const invalidate = () => dispatch => dispatch(resource.invalidate());
export const initialState = {
  details: Object.freeze({}),
  head: Object.freeze({}),
  summaries: Object.freeze({})
};
export const handleHeadSuccess = (state, payload) => ({
  ...state,
  totalCount: parseInt(payload.response.headers.get('x-total-count'), 10)
});
export const handleFetchSuccess = (state, payload) => ({
  ...state,
  data: payload.response.data.reduce((old, current) => ({
    ...old,
    [current.id]: current
  }), state.data),
  [payload.label]: {
    ids: payload.response.data.map(item => item.id),
    totalCount: parseInt(payload.response.headers.get('x-total-count'), 10)
  }
});
export const handleFetchOneSuccess = (state, payload) => ({
  ...state,
  [payload.label]: payload.response.data
});
export const headReducer = createReducer({
  [resource.head.success.getType()]: handleHeadSuccess
}, initialState.head);
export const summariesReducer = createReducer({
  [resource.fetch.success.getType()]: handleFetchSuccess
}, initialState.summaries);
export const detailsReducer = createReducer({
  [resource.fetchOne.success.getType()]: handleFetchOneSuccess
}, initialState.details);
export const reducer = combineReducers({
  details: detailsReducer,
  head: headReducer,
  summaries: summariesReducer
});
const selectRawState = state => get(state, statePath, {});
export const selectRawHead = state => selectRawState(state).head;
export const selectTotalResources = state => selectRawState(state).head.totalCount;
export const selectRawSummaries = state => selectRawState(state).summaries;
export const selectSummariesData = state => selectRawSummaries(state).data;
const createShallowEqualSelector = createSelectorCreator(lruMemoize, shallowequal);
export const selectComputedLabel = createShallowEqualSelector([selectRawSummaries, (_, props) => computeLabel(props)], (raw, label) => label);
export const selectSummariesMeta = (state, props) => selectRawSummaries(state)[selectComputedLabel(state, props)];
export const selectSummariesTotalCount = (state, props) => {
  const meta = selectSummariesMeta(state, props);
  return meta ? meta.totalCount : undefined;
};
export const selectSummaries = createSelector([selectSummariesData, selectSummariesMeta], (data, meta) => meta ? meta.ids.map(id => get(data, id)).filter(Boolean) : undefined);
export const selectRawDetails = state => selectRawState(state).details;
export const selectInstance = (state, props) => selectRawDetails(state)[typeof props === 'string' ? props : props.id];
export const selectAdminUrl = (state, instanceId) => selectInstance(state, instanceId)?.access_information?.admin_url ?? '';
export const selectApplicationDatabaseInfo = (state, instanceId) => selectInstance(state, instanceId)?.access_information?.database;
export const selectApplicationDeployInfo = (state, instanceId) => selectInstance(state, instanceId)?.access_information?.deploy[0];
export const selectCanResetDatabasePassword = (state, instanceId) => selectInstance(state, instanceId)?.database?.name === INSTANCE_DATABASE_MYSQL;
export const selectLogin = (state, instanceId) => selectInstance(state, instanceId)?.access_information?.user ?? '';
export const selectSftpUrl = (state, instanceId) => selectApplicationDeployInfo(state, instanceId)?.sftp.url ?? '';
export const selectIsMultisite = (state, instanceId) => !(selectInstance(state, instanceId)?.language?.single_application ?? true);
export const selectIsSnapshotsEnabled = (state, instanceId) => selectInstance(state, instanceId)?.snapshot_enabled ?? false;
export const selectIsSslFeatureAvailable = (state, instanceId) => (selectInstance(state, instanceId)?.size ?? INSTANCE_SIZE_S).toLowerCase() !== INSTANCE_SIZE_S;
export const selectIsTrial = (state, instanceId) => selectInstance(state, instanceId)?.is_trial ?? false;
export const selectCompatibleApplications = (state, instanceId) => selectInstance(state, instanceId)?.compatible_applications ?? [];
export const selectSiteDeployInfo = createSelector([selectInstance, (_, props) => props.fqdn], (instance, fqdn) => get(instance, 'access_information.deploy', []).filter((deploy = {}) => normalizeFqdn(get(deploy, 'vhost.fqdn', '')) === normalizeFqdn(fqdn))[0]);
export const selectIsFetching = (state, props) => resource.selectIsFetching(state, selectComputedLabel(state, props));
export const selectIsLoaded = (state, props) => resource.selectIsLoaded(state, selectComputedLabel(state, props));
export const selectIsFailure = (state, props) => resource.selectIsFailure(state, selectComputedLabel(state, props));
export const selectIsForbidden = (state, props) => resource.selectIsFailure(state, {
  label: selectComputedLabel(state, props),
  status: 403
});
export const selectIsToRefetch = (state, props) => resource.selectIsToRefetch(state, selectComputedLabel(state, props));
export const selectIsFetchingOne = (state, instanceId) => resource.selectIsFetchingOne(state, instanceId);
export const selectIsLoadedOne = (state, instanceId) => resource.selectIsLoadedOne(state, instanceId);
export const selectIsFailureOne = (state, instanceId) => resource.selectIsFailureOne(state, instanceId);
export const selectIsForbiddenOne = (state, instanceId) => resource.selectIsFailureOne(state, {
  label: instanceId,
  status: 403
});
export const selectIsNotFoundOne = (state, instanceId) => resource.selectIsFailureOne(state, {
  label: instanceId,
  status: 404
});
export const selectIsToRefetchOne = (state, instanceId) => resource.selectIsToRefetchOne(state, instanceId);
export const selectIsCreatingOne = state => resource.selectIsCreatingOne(state, 'default');
export const selectIsPatchingOne = (state, instanceId) => resource.selectIsPatchingOne(state, instanceId);
export const selectIsDeletingOne = (state, instanceId) => resource.selectIsDeletingOne(state, instanceId);
export const selectIsPostingAction = (state, props) => resource.selectIsPosting(state, `${props.id}.${props.action}`);
export const selectIsTogglingSnapshots = (state, instanceId) => selectIsPatchingOne(state, `snapshot-activation-${instanceId}`);